import { BlobItem, TreeRow } from '@/types';

// The folder that contains `path`. Folder paths carry a trailing slash and file
// paths do not; either way we drop the last segment. Root-level items return the
// empty string (the site root).
export function parentFolderPath(path: string): string {
  const trimmed = path.replace(/\/+$/, '');
  const idx = trimmed.lastIndexOf('/');
  return idx === -1 ? '' : trimmed.slice(0, idx + 1);
}

// The folder a drop should land in, given the row under the cursor: a folder
// targets itself, a file targets its parent folder, and no row targets the site
// root.
export function resolveDropFolder(item: BlobItem | null): string {
  if (!item) return '';
  return item.isFolder ? item.path : parentFolderPath(item.path);
}

// Depth-first flatten of the loaded children into the visible rows, descending
// only into expanded folders that actually have children loaded. Children are
// assumed already sorted by the store.
export function flattenTree(
  childrenByPath: Map<string, BlobItem[]>,
  expandedPaths: Set<string>,
): TreeRow[] {
  const rows: TreeRow[] = [];
  const walk = (parentPath: string, depth: number): void => {
    const children = childrenByPath.get(parentPath) || [];
    for (const item of children) {
      const isExpanded = item.isFolder && expandedPaths.has(item.path);
      rows.push({ item, depth, isExpanded });
      if (isExpanded) walk(item.path, depth + 1);
    }
  };
  walk('', 0);
  return rows;
}

// All ancestor folder paths of a path, deepest first (excludes the site root '').
// e.g. 'A/B/x.eml' -> ['A/B/', 'A/'].
export function ancestorsOf(path: string): string[] {
  const out: string[] = [];
  let p = parentFolderPath(path);
  while (p) {
    out.push(p);
    p = parentFolderPath(p);
  }
  return out;
}

// Like flattenTree, but prunes to a reveal set: a row appears only if its path
// is in revealPaths. Used for in-place search, the tree collapses to just the
// matches and the folder paths leading to them, while staying expandable.
export function flattenFiltered(
  childrenByPath: Map<string, BlobItem[]>,
  expandedPaths: Set<string>,
  revealPaths: Set<string>,
): TreeRow[] {
  const rows: TreeRow[] = [];
  const walk = (parentPath: string, depth: number): void => {
    const children = childrenByPath.get(parentPath) || [];
    for (const item of children) {
      if (!revealPaths.has(item.path)) continue;
      const isExpanded = item.isFolder && expandedPaths.has(item.path);
      rows.push({ item, depth, isExpanded });
      if (isExpanded) walk(item.path, depth + 1);
    }
  };
  walk('', 0);
  return rows;
}

export function flattenTreeFrom(
  childrenByPath: Map<string, BlobItem[]>,
  expandedPaths: Set<string>,
  roots: BlobItem[],
): TreeRow[] {
  const rows: TreeRow[] = [];
  const walk = (items: BlobItem[], depth: number): void => {
    for (const item of items) {
      const isExpanded = item.isFolder && expandedPaths.has(item.path);
      rows.push({ item, depth, isExpanded });
      if (isExpanded) walk(childrenByPath.get(item.path) || [], depth + 1);
    }
  };
  walk(roots, 0);
  return rows;
}
